import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { ServiceLocationDTO } from '@core/model';
import { ProviderAccountPgpDTO, ProviderPgpDTO, SecurityService, TableColumn } from '@mynexus/mynexus-ui-lib';
import { SelectItem } from 'primeng/api';

@Component({
    selector: 'app-service-provider',
    templateUrl: './service-provider.component.html',
    styleUrls: ['./service-provider.component.scss'],
    standalone: false
})
export class ServiceProviderComponent implements OnChanges {
  providerCombineData;
  haveProvider: boolean;
  generalInfoColumns = [];
  contactInfoColumns = [];
  networkOption = [];
  serviceProvider: ProviderAccountPgpDTO;
  selectedPgp: ProviderPgpDTO;
  isNetworkEditable = false;
  isNetworkDisabled = false;
  networkValue: string;
  providerAccountId: string;
  isParticipatingNetworksNeeded = false;
  hasNetworkUpdatePermission = false;  


  @Input() isWidgetFirstLevel = false;
  @Input() provider: any;
  @Input() isExternalUser: boolean;
  @Input() networks: any;
  @Input() authRequest: boolean;
  @Input() authNumber: string;
  @Input() serviceLocationDTO: ServiceLocationDTO;
  @Input() reviewMode = false;
  @Input() tenantId: string;
  @Input() tenantName: string;
  @Input() showHistoryTable: boolean;
  @Input() manuallySelectedNetworkId: boolean;
  @Output() manuallySelectedNetworkIdChange = new EventEmitter<boolean>();
  @Output() switchProviderView = new EventEmitter<boolean>();
  @Output() updateProvider = new EventEmitter<ProviderAccountPgpDTO>();
  @Output() selectedProvider = new EventEmitter<ProviderAccountPgpDTO>();


  constructor(private securityService: SecurityService) {
    this.hasNetworkUpdatePermission = this.securityService.hasAccess(['HHServiceProviderNetwork.Update']);
  }


  ngOnChanges(changes: SimpleChanges) {
    if (changes.provider) {
      this.setProvider(changes.provider.currentValue);
    }

    if (changes.isExternalUser) {
      this.initTables();
    }

    if (changes.networks) {
      this.checkNetwork(changes.networks.currentValue);
    }

    this.setDefaultValues();
    if (this.networks || this.provider?.providerPgpDTOList) {
      this.networkOption = this.createNetworkOption(this.networks || this.provider.providerPgpDTOList);
    }

    if (!this.authRequest) {
      this.isParticipatingNetworksNeeded = !this.providerCombineData?.participatingNetworks
        && this.networkOption?.length > 0 && !this.providerCombineData?.providerAccountId;
    }
  }

  setProvider(value) {
    this.providerAccountId = value?.providerPgpDTO?.providerAccountId;
    if (value) {
      this.providerCombineData = value?.providerAccountDTO;
      if (value.providerPgpDTO) {
        this.providerCombineData = {
          ...this.providerCombineData,
          clientProviderId: this.providerCombineData.accountId,
          ...value.providerPgpDTO,
          portalProviderId: value.providerPgpDTO.accountId
        };
      } else if (value.providerPgpDTOList) {
        this.providerCombineData = {
          ...this.providerCombineData,
          ...value.providerPgpDTOList[0],
          portalProviderId: value.providerPgpDTOList[0].accountId
        };
      }
    }
  }

  initTables() {
    this.generalInfoColumns =
      [
        new TableColumn('Parent Company Name', 'parentCompany', '15rem'),
        new TableColumn('NPI', 'sequenceNumber', 'npi', '10rem'),
        new TableColumn('Network', 'network', '14rem'),
        new TableColumn('Out of Network', 'participationStatus', '10rem'),
        new TableColumn('Tax ID', 'type', 'taxId', '8rem'),
        new TableColumn('CMS Number', 'cmsnumber', '10rem'),
        new TableColumn('Medicaid Number', 'medicaidNumber', '10rem')
      ]

    this.contactInfoColumns = [
      new TableColumn('Street', 'physicalStreet'),
      new TableColumn('City', 'physicalCity'),
      new TableColumn('State', 'physicalState'),
      new TableColumn('ZIP Code', 'physicalZip')
    ];
  }

  switchToProviderSelection() {
    this.switchProviderView.emit(false);
  }

  createNetworkOption(providerPgp: ProviderPgpDTO[]): SelectItem[] {
    if (providerPgp) {
      let optBuf = providerPgp
        .filter(el => el != null && el !== undefined)
        .map(el => ({label: el.participatingNetworks || this.tenantName || 'N/A', value: el.providerPgpId}));
      let labelsBuf = [];
      let returnBuf = [];
      for (let i=0; i<optBuf.length; i++) {
        if (labelsBuf.indexOf(optBuf[i].label) === -1) {
          labelsBuf.push(optBuf[i].label);
          returnBuf.push(optBuf[i]);
        }
      }
      return returnBuf;
    }
  }

  selectNetwork(event) {
    const selectedValue = event?.value ?? event;
    this.selectedPgp = this.networks
      ? this.networks?.filter(el => el && el.providerPgpId === selectedValue)[0]
      : this.provider.providerPgpDTOList?.filter(el => el && el.providerPgpId === selectedValue)[0];
    this.providerCombineData = {
      ...this.provider.providerAccountDTO,
      ...this.selectedPgp
    };

    this.manuallySelectedNetworkIdChange.emit(true);
    this.selectedNetworkChange();
  }

  checkNetwork(network) {
    if ( network?.length > 1) {
      this.isNetworkDisabled = false;
    } else {
      this.isNetworkDisabled = true;
    }
  }

  updateCurrentAuthorization() {
    this.provider.providerPgpDTO = this.selectedPgp;
    this.updateProvider.emit(this.provider);
  }

  selectedNetworkChange() {
    this.serviceProvider = {providerAccountDTO: this.provider?.providerAccountDTO, providerPgpDTO: this.selectedPgp};
    this.selectedProvider.emit(this.serviceProvider);
  }

  setDefaultValues() {
    const isAllDataValid = this.providerCombineData && this.networks && (typeof this.authRequest !== 'undefined');

    if (!Boolean(isAllDataValid) || this.providerCombineData?.providerPgpId === this.networkValue) {
      return;
    }

    if ((this.authRequest && (this.networks.length === 1 || this.manuallySelectedNetworkId)) || (!this.authRequest && this.networks)) {
      const provider = this.networks.filter(item => item && item.providerPgpId === this.providerCombineData?.providerPgpId);

      if (provider?.length !== 0) {
        this.networkValue =  provider[0]?.providerPgpId;
        this.selectedPgp = provider[0];
      }

      this.selectedNetworkChange();
    }
  }
}
